import React, { useEffect, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import DataTable from "../../../../../Common/DataTable/DataTable";
import DataTableHeader from "../../../../../Common/DataTableHeader/DataTableHeader";
import DataTableFooter from "../../../../../Common/DataTableFooter/DataTableFooter";
import { dateWiseOccColumns } from "./DateWiseOccData";
import { setCurrentPage } from "../../../../../Redux/slice/features/paginationSilce";
import { REACT_APP_API_URL } from "../../../../../env";
import { fetchUserDetails, rehydrateAuthState } from "../../../../../Redux/slice/features/authSlice";

const DateWiseOccTable = () => {
  const today = new Date().toISOString().split("T")[0];
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { currentPage, itemsPerPage } = useSelector((state) => state.pagination);
  const [data, setData] = useState([]);
  const [hotelOptions, setHotelOptions] = useState([]);
  const [filters, setFilters] = useState({
    fromDate: today,
    toDate: today,
    hotelId: 0,
  });
  const [dateError, setDateError] = useState(""); 

  const userHotelId = user?.user?.PropertyID;

  useEffect(() => {
    dispatch(rehydrateAuthState());
    dispatch(fetchUserDetails());
  }, [dispatch]);

  useEffect(() => {
    dispatch(setCurrentPage(1));
  }, [dispatch]);

  const fetchHotels = useCallback(async () => {
    try {
      const url = userHotelId
        ? `${REACT_APP_API_URL}api/dashboard/admin/hotel/${userHotelId}`
        : `${REACT_APP_API_URL}api/dashboard/admin/hotels`;

      const response = await axios.get(url);
      const hotels = Array.isArray(response.data.data)
        ? response.data.data
        : [response.data.data];

      const formatted = hotels.map((h) => ({
        value: h.PropertyID,
        label: h.UnitName || h.PropertyName,
      }));

      setHotelOptions(formatted);

      if (userHotelId && formatted.length > 0) {
        setFilters((f) => ({ ...f, hotelId: userHotelId }));
      }
    } catch (err) {
      console.error("Error fetching hotels:", err);
    }
  }, [userHotelId]);

  useEffect(() => {
    fetchHotels();
  }, [fetchHotels]);

  const fetchReport = useCallback(async () => {
    const HotelID = userHotelId ?? filters.hotelId;
    try {
      const res = await axios.post(`${REACT_APP_API_URL}api/dashboard/admin/datewise-occ`, {
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        hotelId: HotelID,
        dtInterval: 0,
      });

      const formatted = res.data.data.map((item, index) => ({
        Sl: index + 1 + (currentPage - 1) * itemsPerPage,
        PropertyName: item.PropertyName || "-",
        CustomerName: item.CustomerName || "-",
        BookingDateTime: item.BillDate || "-",
        CheckinDate: item.CheckinDate || "-",
        CheckoutDate: item.CheckOutDate || "-",
        BookingRefNo: item.BookingRefNo || "-",
        NoOfRooms: item.TotalNoOfRoom || 0,
        PaymentMode: item.PaymentMode || "-",
        TransactionNumber: item.ChequeDDNo || "-",
        BillDate: item.IssueDate || "-",
        BookingSource: item.ChildAge1 || "-",
        MRNumber: item.vchMRNumber || "-",
        MRDate: item.CheckOutTime || "-",
        CustomerMobile: item.MobileNo || "-",
        BaseAmount: `₹ ${item.Amount?.toFixed(2)}`,
        BookedBy: item.PropertyName || "-",
        TaxType: item.TaxType || "-",
        TaxAmount: `₹ ${item.TaxAmount?.toFixed(2)}`,
        TotalAmount: `₹ ${item.TotalAmount?.toFixed(2)}`,
      }));

      setData(formatted);
    } catch (err) {
      console.error("Error fetching report:", err);
      setData([]);
    }
  }, [userHotelId, filters, currentPage, itemsPerPage]);

  const handleShow = useCallback(() => {
    if (!filters.fromDate || !filters.toDate) {
      setDateError("Please select From Date and To Date.");
      return;
    }
    if (new Date(filters.fromDate) > new Date(filters.toDate)) {
      setDateError("From Date cannot be after To Date.");
      return;
    }
    setDateError("");
    dispatch(setCurrentPage(1));
    fetchReport();
  }, [filters, dispatch, fetchReport]);

  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return data.slice(start, start + itemsPerPage);
  }, [data, currentPage, itemsPerPage]);

  const handleNextPage = useCallback(() => {
    dispatch(setCurrentPage(currentPage + 1));
  }, [dispatch, currentPage]);

  const handlePreviousPage = useCallback(() => {
    dispatch(setCurrentPage(currentPage - 1));
  }, [dispatch, currentPage]);

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <input
          type="date"
          value={filters.fromDate}
          onChange={(e) => setFilters((f) => ({ ...f, fromDate: e.target.value }))}
          className="border p-2 rounded"
          max={filters.toDate || undefined}
          style={{marginRight:'10px'}}
        />
        <input
          type="date"
          value={filters.toDate}
          onChange={(e) => setFilters((f) => ({ ...f, toDate: e.target.value }))}
          className="border p-2 rounded"
          min={filters.fromDate || undefined}
          style={{marginRight:'10px'}}
        />
        <select
          value={filters.hotelId}
          onChange={(e) =>
            setFilters((f) => ({ ...f, hotelId: Number(e.target.value) }))
          }
          className="border p-2 rounded"
        >
          <option value="0">-- Select Hotel --</option>
          {hotelOptions.map((hotel) => (
            <option key={hotel.value} value={hotel.value}>
              {hotel.label}
            </option>
          ))}
        </select>
        <button
          onClick={handleShow}
          className="btn btn-primary text-white rounded px-12 py-2 m-2"
        >
          Show
        </button>
      </div>
      {dateError && (
        <div className="alert alert-danger py-2 mb-2">{dateError}</div>
      )}

      <DataTableHeader columns={dateWiseOccColumns} data={data} />
      <div className="table-responsive">
        <DataTable
          columns={dateWiseOccColumns}
          data={currentItems}
          enablePagination={false}
        />
      </div>
      <DataTableFooter
        dataT={data}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        handleNextPage={handleNextPage}
        handlePreviousPage={handlePreviousPage}
      />
    </div>
  );
};

export default DateWiseOccTable;
